import * as React from 'react'
import { redirect } from 'react-router-dom'

import { Download, Save } from '@factorialco/f0-react/icons/app'
import { DataCatalogDocument } from 'generated/resources/graphql'
import { ReportDetailDocument } from 'generated/resources/graphql'

import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { MetaContext } from 'modules/core/lib/navigation/routes'
import { ResourceMeta } from 'modules/core/lib/navigation/types'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSlugFromTableId } from 'modules/ai_reports/lib/slug-tableid-mapping'
import DataCatalogPage from 'modules/ai_reports/pages/DataCatalogPage'
import ReportDetailPage from 'modules/ai_reports/pages/ReportDetailPage'
import ReportsListPage from 'modules/ai_reports/pages/ReportsListPage'

const AiReportsPage = React.lazy(() => import('../pages/ReportCreatePage'))
const AiReportsRootPage = React.lazy(() => import('../pages/AiReportsRootPage'))

export const AiReportsRoutes: NavigationRoutesFn = (navigation) => {
  const buildSubNavigation = () => [
    {
      id: 'reports',
      title: i18n.t('ai_reports.reports.title'),
      href: navigation.aiReports.reports.fullPath(),
      isVisible: true,
    },
    {
      id: 'data-catalog',
      title: i18n.t('ai_reports.data_catalog.title'),
      href: navigation.aiReports.catalog.fullPath(),
      isVisible: true,
    },
  ]

  const buildReportHeaderMeta = (
    slug: string | undefined,
    context: MetaContext,
    resourceMeta: ResourceMeta
  ) => {
    return context
      .watchQuery({
        query: DataCatalogDocument,
        variables: { companyId: context.session.company.id },
      })
      .map((result) => {
        const table = result.data?.iris.factTablesConnection.nodes.find(
          (node) => getSlugFromTableId(node.id) === slug
        )

        return {
          ...resourceMeta,
          headerComponentProps: {
            ...resourceMeta?.headerComponentProps,
            metadata: [
              table && {
                label: i18n.t('ai_reports.data_catalog.dataset'),
                value: {
                  type: 'text',
                  content: table.name,
                },
              },
              ...(resourceMeta?.headerComponentProps?.metadata ?? []),
            ],
          },
        }
      })
  }

  return [
    {
      path: navigation.aiReports.path,
      element: <AiReportsRootPage />,

      handle: {
        module: 'ai-reports',
        moduleName: () => i18n.t('sidebar.ai_reports'),
        meta: () => {
          return {
            title: i18n.t('ai_reports.section_name'),
            description: i18n.t('ai_reports.section_description'),
            subNavigation: buildSubNavigation(),
          }
        },
      },
      children: [
        {
          index: true,
          loader: () => {
            return redirect(navigation.aiReports.reports.fullPath())
          },
        },
        {
          path: navigation.aiReports.reports.path,
          handle: {
            meta: () => ({
              title: 'Reports',
              description: 'Reports',
            }),
          },
          children: [
            {
              index: true,
              element: <ReportsListPage />,
            },
            {
              path: navigation.aiReports.reports.reportId.path,
              element: <ReportDetailPage />,
              handle: {
                resource: true,
                meta: (context) => {
                  return context
                    .watchQuery({
                      query: ReportDetailDocument,
                      variables: {
                        reportId: context.params.reportId || '',
                        companyId: context.session.company.id,
                      },
                    })
                    .map(({ data }) => {
                      const report = data?.analytics.report
                      return {
                        title: report?.name || 'Error',
                        headerComponentProps: {
                          title: report?.name,
                          description: report?.description,
                          primaryAction: {
                            label: i18n.t('ai_reports.save_modal.save'),
                            icon: Save,
                            key: 'save',
                          },
                          secondaryActions: [
                            {
                              label: i18n.t('ai_reports.export'),
                              key: 'export',
                              icon: Download,
                            },
                          ],
                          metadata: report?.datasource && [
                            {
                              label: i18n.t('ai_reports.reports.created_by'),
                              value: {
                                text: `${report.author?.firstName} ${report.author?.lastName}`,
                                type: 'avatar',
                                variant: {
                                  firstName: report.author?.firstName ?? '',
                                  lastName: report.author?.lastName ?? '',
                                  src: buildUrl(report.author?.avatar?.url ?? ''),
                                  type: 'person',
                                },
                              },
                            },
                            {
                              label: i18n.t('ai_reports.reports.created_at'),
                              value: {
                                type: 'date',
                                formattedDate: dateBackendDateTimeToUI(report.createdAt ?? ''),
                              },
                            },
                            {
                              label: i18n.t('ai_reports.reports.last_update'),
                              value: {
                                type: 'date',
                                formattedDate: dateBackendDateTimeToUI(report.updatedAt ?? ''),
                              },
                            },
                            {
                              label: i18n.t('ai_reports.reports.datasource'),
                              value: {
                                type: 'text',
                                content: report.datasource.name,
                              },
                            },
                          ],
                        },
                      }
                    })
                },
              },
            },
          ],
        },
        {
          path: navigation.aiReports.reports.reportId.path,
          element: <ReportDetailPage />,
        },
        {
          path: navigation.aiReports.catalog.path,
          handle: {
            meta: () => ({
              title: i18n.t('ai_reports.data_catalog.title'),
              description: i18n.t('ai_reports.data_catalog.description'),
            }),
          },
          children: [
            {
              index: true,
              element: <DataCatalogPage />,
            },
            {
              path: navigation.aiReports.catalog.slug.path,
              element: <DataCatalogPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('ai_reports.reports.title'),
                }),
              },
              children: [
                {
                  path: navigation.aiReports.catalog.slug.new.path,
                  element: <AiReportsPage />,
                  handle: {
                    resource: true,
                    meta: (context) =>
                      buildReportHeaderMeta(context.params.slug, context, {
                        title: i18n.t('ai_reports.reports.new_exploration'),
                        headerComponentProps: {
                          title: i18n.t('ai_reports.reports.new_exploration'),
                          primaryAction: {
                            label: i18n.t('ai_reports.save_modal.save'),
                            icon: Save,
                            key: 'save',
                          },
                          secondaryActions: [
                            {
                              label: i18n.t('ai_reports.export'),
                              icon: Download,
                              key: 'export',
                            },
                          ],
                        },
                      }),
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}
